// Hand-written domain + request/response types for the ai-gateway REST API.
// Endpoint contract source: FLOWX_5.x → INTEGRATION DESIGNER → ai-gateway (Postman).

// ─── Enums ────────────────────────────────────────────────────────────────────

export enum ConversationState {
  Finished = 'FINISHED',
  Ready = 'READY',
  Unknown = 'UNKNOWN',
}

export enum ConversationThreadState {
  Kept = 'KEPT',
  Discarded = 'DISCARDED',
  Processing = 'PROCESSING',
  Ready = 'READY',
}

export enum ConversationThreadMessageState {
  Cancelled = 'CANCELLED',
  Finished = 'FINISHED',
  Pending = 'PENDING',
  Processing = 'PROCESSING',
  Unknown = 'UNKNOWN',
}

export enum ConversationThreadExecutionPlanActionState {
  Cancelled = 'CANCELLED',
  Executing = 'EXECUTING',
  Finished = 'FINISHED',
  Pending = 'PENDING',
}

export enum ConversationThreadExecutionPlanActionType {
  Automatic = 'AUTOMATIC',
  HumanActionable = 'HUMAN_ACTIONABLE',
  Unknown = 'UNKNOWN',
}

export enum ConversationOutcomeType {
  Document = 'DOCUMENT',
  Error = 'ERROR',
  ExternalReference = 'EXTERNAL_REFERENCE',
  KgReference = 'KG_REFERENCE',
  Multiple = 'MULTIPLE',
  NodeConfig = 'NODE_CONFIG',
  RequiresMoreInfo = 'REQUIRES_MORE_INFO',
  Textual = 'TEXTUAL',
  Unknown = 'UNKNOWN',
}

export enum ConversationOutcomeClassification {
  Chat = 'CHAT',
  Error = 'ERROR',
  Interrupt = 'INTERRUPT',
  Logging = 'LOGGING',
  Main = 'MAIN',
  Reasoning = 'REASONING',
  Unknown = 'UNKNOWN',
}

export enum ConversationOutcomeState {
  Created = 'CREATED',
  Discarded = 'DISCARDED',
  Kept = 'KEPT',
  Unknown = 'UNKNOWN',
}

export enum ConversationThreadExecutionPlanActionTargetType {
  BusinessRule = 'BUSINESS_RULE',
  CustomComponent = 'CUSTOM_COMPONENT',
  Expression = 'EXPRESSION',
  Node = 'NODE',
  Process = 'PROCESS',
  ReusableFunction = 'REUSABLE_FUNCTION',
  ReusableUiTemplate = 'REUSABLE_UI_TEMPLATE',
  UiFlow = 'UI_FLOW',
  ProjectDataModel = 'PROJECT_DATA_MODEL',
  WorkflowNode = 'WORKFLOW_NODE',
}

export enum ConversationThreadExecutionPlanActionTargetExpressionType {
  Disable = 'DISABLE',
  Hide = 'HIDE',
  Unknown = 'UNKNOWN',
}

export enum ConversationDocumentAttentionEntryType {
  BusinessRule = 'BUSINESS_RULE',
  Process = 'PROCESS',
  ScreenExpression = 'SCREEN_EXPRESSION',
  ScreenName = 'SCREEN_NAME',
  Unknown = 'UNKNOWN',
}

export enum ConversationThreadContextDataItemType {
  ApplicationReference = 'APPLICATION_REFERENCE',
  BinaryObject = 'BINARY_OBJECT',
  ConversationThread = 'CONVERSATION_THREAD',
  DocumentResourceReference = 'DOCUMENT_RESOURCE_REFERENCE',
  GenericRaw = 'GENERIC_RAW',
  GenericString = 'GENERIC_STRING',
  KnowledgebaseReference = 'KNOWLEDGEBASE_REFERENCE',
  ProcessReference = 'PROCESS_REFERENCE',
  TaskReference = 'TASK_REFERENCE',
  Unknown = 'UNKNOWN',
}

export enum ConversationSenderRole {
  User = 'USER',
  Agent = 'AGENT',
  System = 'SYSTEM',
  Unknown = 'UNKNOWN',
}

// ─── Common references ────────────────────────────────────────────────────────

export interface AuditData {
  createdAt?: string | null
  createdBy?: string | null
  lastUpdateAt?: string | null
  lastUpdateBy?: string | null
}

export interface ProcessReference {
  id: string
  applicationId: string
  versionId?: string | null
}

export interface ApplicationReference {
  applicationId: string
  versionId?: string | null
}

export interface BinaryObjectReference {
  docID?: string | null
  tempFileId?: string | null
  fileName?: string | null
  mimeType?: string | null
  attention?: Record<string, unknown> | null
}

export interface ContextItem {
  key: string
  itemType: ConversationThreadContextDataItemType
  valueProcessReference?: ProcessReference | null
  valueApplicationReference?: ApplicationReference | null
  valueBinaryObject?: BinaryObjectReference | null
  valueGenericString?: string | null
  valueGenericRaw?: string | null
}

export interface ContextData {
  items: ContextItem[]
}

export interface ConversationDocumentReferenceInput {
  docID: string
  attention?: ConversationDocumentAttentionInput | null
}

export interface ConversationDocumentAttentionInput {
  entries: ConversationDocumentAttentionEntryInput[]
}

export interface ConversationDocumentAttentionEntryInput {
  type: ConversationDocumentAttentionEntryType
  process?: { name?: string } | null
  screenName?: { name?: string } | null
  screenExpression?: { name?: string } | null
  businessRule?: { name?: string } | null
  startPage?: number | null
  endPage?: number | null
}

// ─── Execution plan ───────────────────────────────────────────────────────────

export interface ActionAgent {
  agent: string
  action: string
}

export interface ExecutionPlanTargetNode {
  nodeType?: string | null
  id?: string | null
  navigationAreaId?: string | null
  templateConfigId?: string | null
}

export interface ExecutionPlanTargetBusinessRule {
  nodeType?: string | null
  nodeId?: string | null
  actionId?: string | null
  index?: number | null
}

export interface ExecutionPlanTargetExpression {
  nodeId?: string | null
  expressionType?: string | null
  elementId?: string | null
}

export interface ExecutionPlanTargetCustomComponent {
  name?: string | null
  identifier?: string | null
}

export interface ExecutionPlanTargetProcess {
  applicationId?: string | null
  applicationVersion?: string | null
  id?: string | null
}

export interface ExecutionPlanTargetWorkflowNode {
  id?: string | null
  aiNodeType?: string | null
  workflowId?: string | null
}

export interface ExecutionPlanTargetUiFlow {
  uiFlowResourceId?: string | null
  templateConfigId?: string | null
}

export interface ActionTarget {
  name: string | null
  // `null` when the target type isn't yet known.
  targetType: ConversationThreadExecutionPlanActionTargetType | null
  node?: ExecutionPlanTargetNode | null
  process?: ExecutionPlanTargetProcess | null
  businessRule?: ExecutionPlanTargetBusinessRule | null
  expression?: ExecutionPlanTargetExpression | null
  customComponent?: ExecutionPlanTargetCustomComponent | null
  workflowNode?: ExecutionPlanTargetWorkflowNode | null
  uiFlow?: ExecutionPlanTargetUiFlow | null
}

export interface ExecutionPlanAction {
  localId: string
  actionType: ConversationThreadExecutionPlanActionType
  // Optional on the request side (hints/initial plans): the server fills in
  // the initial state (typically `PENDING`). Always populated on responses.
  state?: ConversationThreadExecutionPlanActionState
  agent: ActionAgent
  name: string
  description: string
  recommendedPrompt?: string | null
  parallel?: boolean | null
  target: ActionTarget
  context: ContextData
}

export interface ExecutionPlan {
  id: string
  conversationId?: string | null
  actions: ExecutionPlanAction[]
}

export interface ConversationMessageHint {
  autoStart: boolean
  // Server defaults to false when absent on request; always present on responses.
  externallyComputedPlan?: boolean
  agent?: string | null
  actions: ExecutionPlanAction[]
}

// ─── Outcomes ─────────────────────────────────────────────────────────────────

export interface ConversationOutcomeTextual {
  content: string
  contextData?: ContextData
}

// Request-side counterpart used by `POST .../outcomes` — wire field is `value`,
// not `content`.
export interface ConversationOutcomeTextualInput {
  value: string
}

export interface ConversationOutcomeDocument {
  documentReferences: ConversationDocumentReferenceInput[]
}

export interface ConversationOutcomeError {
  error?: {
    code?: string | null
    message?: string | null
    details?: Record<string, unknown> | null
  } | null
}

export interface ConversationOutcomeKgReference {
  nodes: unknown[]
}

export interface ConversationOutcomeExternalReference {
  flowXApplication?: unknown
  flowxBusinessRule?: unknown
  flowxProcess?: unknown
  flowxServiceTask?: unknown
  flowxUserTask?: unknown
  generic?: unknown
}

export interface ConversationOutcomeMultiple {
  outcomes: ConversationOutcome[]
}

export interface ConversationOutcomeNodeConfig {
  instructions?: string | null
  context?: string | null
  jsonSchema?: string | null
}

export interface ConversationOutcomeRequiresMoreInformation {
  foo?: string | null
}

export interface ConversationOutcome {
  id?: string | null
  // Response wire names. Request bodies use the shorter `type` /
  // `classification` form — see `AppendOutcomeRequest`.
  outcomeType: ConversationOutcomeType
  outcomeClassification: ConversationOutcomeClassification
  state?: ConversationOutcomeState
  textual?: ConversationOutcomeTextual | null
  document?: ConversationOutcomeDocument | null
  error?: ConversationOutcomeError | null
  kgReference?: ConversationOutcomeKgReference | null
  externalReference?: ConversationOutcomeExternalReference | null
  multiple?: ConversationOutcomeMultiple | null
  nodeConfig?: ConversationOutcomeNodeConfig | null
  requiresMoreInformation?: ConversationOutcomeRequiresMoreInformation | null
  auditData?: AuditData | null
}

// ─── Domain entities (response-side) ──────────────────────────────────────────

export interface ConversationThreadOutcomeContainer {
  outcomes: ConversationOutcome[]
  auditData?: AuditData | null
}

export interface ConversationThread {
  id: string
  state: ConversationThreadState
  finalizedAt?: string | null
  finalizedReason?: string | null
  contextData: ContextData
  // Populated on the single-fetch path (`GET /conversations/:id`); empty on
  // list endpoints.
  messages?: Message[]
  outcome?: ConversationThreadOutcomeContainer | null
  auditData?: AuditData | null
}

export interface Message {
  id: string
  conversationId: string
  threadId: string
  messageId: string
  orgId?: string | null
  workspaceId?: string | null
  sequenceNumber: number
  userId?: string | null
  senderRole: ConversationSenderRole
  prompt: string
  displayMessage: string
  state: ConversationThreadMessageState
  contextData: ContextData
  documents: ConversationDocumentReferenceInput[]
  hint?: ConversationMessageHint | null
  executionPlan?: ExecutionPlan | null
  // Server wraps outcomes as `{outcomes: [...]}`, not a bare array.
  outcome?: ConversationThreadOutcomeContainer | null
  auditData?: AuditData | null
}

export interface Conversation {
  id: string
  orgId: string
  workspaceId: string
  userId: string
  userEmail: string
  businessCaseId?: string | null
  applicationId: string
  appVersionId?: string | null
  processResourceId: string
  state: ConversationState
  threads: ConversationThread[]
  defaultThreadId: string
  contextData: ContextData
  documents: ConversationDocumentReferenceInput[]
  name?: string | null
  locale?: string | null
  llmModelId?: string | null
  closedAt?: string | null
  auditData?: AuditData | null
}

// ─── Request bodies ───────────────────────────────────────────────────────────

export interface CreateConversationRequest {
  applicationId: string
  processResourceId: string
  message?: string | null
  contextData?: ContextData
  documents?: ConversationDocumentReferenceInput[]
  hint?: ConversationMessageHint | null
  locale?: string | null
  llmModelId?: string | null
}

export interface AddThreadRequest {
  message?: string | null
  contextData?: ContextData
  documents?: ConversationDocumentReferenceInput[]
  hint?: ConversationMessageHint | null
}

export interface AddThreadMessageRequest {
  message?: string | null
  contextData?: ContextData
  documents?: ConversationDocumentReferenceInput[]
  hint?: ConversationMessageHint | null
}

export interface KeepThreadOutcomeRequest {
  messageId?: string | null
  outcomeIds?: string[]
  shouldFinalize: boolean
}

export interface DiscardThreadOutcomeRequest {
  messageId?: string | null
  outcomeIds?: string[]
  reason?: string | null
  shouldFinalize: boolean
}

export interface PatchConversationContextRequest {
  context: ContextData
  conversationState?: ConversationState | null
}

export interface PatchThreadContextRequest {
  context: ContextData
  threadState?: ConversationThreadState | null
}

export interface PatchMessageContextRequest {
  context: ContextData
  threadState?: ConversationThreadState | null
  messageState?: ConversationThreadMessageState | null
}

export interface PatchPlanActionRequest {
  state?: ConversationThreadExecutionPlanActionState
  target?: ActionTarget | null
  context?: ContextData
}

export interface ReplaceMessageExecutionPlanRequest {
  newMessage: string
  contextData: ContextData
  executionPlan: { actions: ExecutionPlanAction[] }
}

export interface AppendOutcomeRequest {
  id?: string | null
  type: ConversationOutcomeType
  classification: ConversationOutcomeClassification
  textual?: ConversationOutcomeTextualInput | null
  document?: ConversationOutcomeDocument | null
  kgReference?: ConversationOutcomeKgReference | null
  externalReference?: ConversationOutcomeExternalReference | null
  error?: ConversationOutcomeError | null
  multiple?: ConversationOutcomeMultiple | null
  requiresMoreInformation?: ConversationOutcomeRequiresMoreInformation | null
}

// ─── Response shapes ──────────────────────────────────────────────────────────

export interface CreateConversationResponse {
  id: string
  defaultThreadId: string
  conversation: Conversation
  seedMessage?: Message
}

// `POST /conversations/:id/threads` returns `{thread, message}` — the full
// freshly-opened thread plus its seed message.
export interface AddThreadResponse {
  thread: ConversationThread
  message: Message
}

// `POST /conversations/:id/threads/:tid/messages` returns the bare
// Message (no `{message: …}` envelope).
export type AddThreadMessageResponse = Message

// `POST /conversations/:id/threads/:tid/messages/:mid/outcomes` returns the
// bare ConversationOutcome (no `{outcome: …}` envelope).
export type AppendOutcomeResponse = ConversationOutcome

export interface KeepConversationThreadOutcomeExecutionPlanChange {
  conversationId: string
  threadId: string
  executionPlan: ExecutionPlan
}

export interface KeepThreadOutcomeResponse {
  pendingChanges: KeepConversationThreadOutcomeExecutionPlanChange[]
}

// URL-parameter shape for the conversation message stream endpoint
// (`GET ai-gateway/api/conversations/:id/threads/:tid/messages/:mid/stream`).
// Auth + workspace are carried by HTTP headers, not the payload.
export interface StreamMessageRequest {
  conversationId: string
  threadId: string
  messageId: string
}

// Response shape for `PATCH /…/plan/actions/:actionLocalId`.
// `message` is always the post-patch parent message (refreshed plan + outcomes).
// `child` is populated when the server spawned a dedicated implementation
// thread for the action (PENDING → EXECUTING with `spawnImplementationThread`
// defaulting to `true`); it carries the fresh thread, its seed message
// (containing the cloned action), and the local id of the cloned action.
export interface PatchPlanActionResponse {
  message: Message
  child?: {
    thread: ConversationThread
    seedMessage: Message
    actionLocalId: string
  }
}

export interface PaginatedConversations {
  items: Conversation[]
  page?: number
  size?: number
  totalCount?: number
}

export interface PaginatedMessages {
  items: Message[]
  page?: number
  size?: number
  totalCount?: number
}

export type ConversationApiError = Error | null
